/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Button, Modal } from 'antd';
import { useMemo, useState } from 'react';

import { Node, NodeConfigMode } from '@/client';
import { NodeMode } from '@/components/States/NodeMode';
import { useMessage } from '@/hooks/message';
import { useBulkAssignConfigsMutation } from '@/hooks/mutation/useBulkAssignConfigsMutation';
import { useBulkEnableMaintenanceModeMutation } from '@/hooks/mutation/useBulkEnableMaintenanceModeMutation';
import { useGetNodesPreviousConfigMutation } from '@/hooks/mutation/useGetNodesPreviousConfigMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Space } from '@/primitives/Space';
import { Table } from '@/primitives/Table';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

export type ToggleMaintenanceModeModalProps = {
  selectedNodes: Node[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type MaintenanceModePreviewRow = {
  nodeId: string;
  hostname: string;
  currentMode: NodeConfigMode;
};

export function ToggleMaintenanceModeModal({
  selectedNodes,
  open,
  onClose,
  onSuccess,
}: ToggleMaintenanceModeModalProps) {
  const { t } = useMimicTranslation('nodes');
  const [message] = useMessage();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: bulkEnableMaintenanceMode } = useBulkEnableMaintenanceModeMutation({});
  const { mutateAsync: bulkAssignConfigs } = useBulkAssignConfigsMutation({});
  const { mutateAsync: getNodesPreviousConfig } = useGetNodesPreviousConfigMutation({});

  const modes = useMemo(() => selectedNodes.map((node) => node.appliedNodeConfigMode ?? 'standard'), [selectedNodes]);

  const allStandard = modes.every((mode) => mode === 'standard');
  const allMaintenance = modes.every((mode) => mode === 'maintenance');
  const isMixed = !allStandard && !allMaintenance;

  const previewRows = useMemo<MaintenanceModePreviewRow[]>(
    () =>
      selectedNodes.map((node) => ({
        nodeId: node.id,
        hostname: node.hostname ?? node.name ?? node.id,
        currentMode: node.appliedNodeConfigMode ?? 'standard',
      })),
    [selectedNodes],
  );

  const columns = useMemo(
    () => [
      {
        title: t('toggleMaintenanceModeModal.hostname'),
        dataIndex: 'hostname',
        key: 'hostname',
      },
      {
        title: t('toggleMaintenanceModeModal.currentMode'),
        dataIndex: 'currentMode',
        key: 'currentMode',
        render: (mode: NodeConfigMode) => <NodeMode mode={mode} />,
      },
    ],
    [t],
  );

  const handleEnterMaintenance = async () => {
    setIsLoading(true);
    const nodeIds = selectedNodes.map((node) => node.id);

    try {
      const maintenanceResult = await bulkEnableMaintenanceMode({ nodeIds });

      const assignments = maintenanceResult.data.map((item) => ({
        nodeId: item.nodeId,
        configId: item.maintenanceConfigId,
        revisionNumber: 1,
      }));

      await bulkAssignConfigs({ assignments });
      message.success(t('toggleMaintenanceModeModal.enterSuccess'));
      onSuccess();
    } catch {
      message.error(t('toggleMaintenanceModeModal.enterError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExitMaintenance = async () => {
    setIsLoading(true);
    const nodeIds = selectedNodes.map((node) => node.id);

    try {
      const previousConfigResult = await getNodesPreviousConfig({ nodeIds });

      const assignments = previousConfigResult.data.map((item) => ({
        nodeId: item.nodeId,
        configId: item.configId,
        revisionNumber: item.revisionNumber,
      }));

      await bulkAssignConfigs({ assignments });
      message.success(t('toggleMaintenanceModeModal.exitSuccess'));
      onSuccess();
    } catch {
      message.error(t('toggleMaintenanceModeModal.exitError'));
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <Space>
      <Button onClick={onClose}>{t('toggleMaintenanceModeModal.cancel')}</Button>
      <Button
        type="primary"
        disabled={!allMaintenance || isLoading}
        loading={isLoading && allMaintenance}
        onClick={handleExitMaintenance}
      >
        {t('toggleMaintenanceModeModal.exitButton')}
      </Button>
      <Button
        type="primary"
        disabled={!allStandard || isLoading}
        loading={isLoading && allStandard}
        onClick={handleEnterMaintenance}
      >
        {t('toggleMaintenanceModeModal.enterButton')}
      </Button>
    </Space>
  );

  return (
    <Modal open={open} onCancel={onClose} destroyOnClose footer={footer}>
      <Space dir="vertical" size="md" width="100%">
        <Title level={4}>{t('toggleMaintenanceModeModal.title')}</Title>
        {isMixed && <Text>{t('toggleMaintenanceModeModal.mixedSelectionWarning')}</Text>}
        <Table dataSource={previewRows} columns={columns} rowKey="nodeId" pagination={{ pageSize: 10 }} />
      </Space>
    </Modal>
  );
}
